import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface FieldFrameProps {
  controlId: string
  label: string
  hint?: string
  error?: string
  required?: boolean
  hintId: string
  errorId: string
  /** Conteúdo alinhado à direita das mensagens (ex.: contador de caracteres). */
  meta?: ReactNode
  className?: string
  children: ReactNode
}

/**
 * Moldura compartilhada por Input, Textarea e Select: rótulo sempre visível,
 * dica e erro ligados ao controle por `aria-describedby`.
 */
export function FieldFrame({
  controlId,
  label,
  hint,
  error,
  required,
  hintId,
  errorId,
  meta,
  className,
  children,
}: FieldFrameProps) {
  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      <label
        htmlFor={controlId}
        className="text-sm font-medium leading-5 text-[var(--text-primary)]"
      >
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="ml-0.5 text-[var(--danger)]">
              *
            </span>
            <span className="sr-only"> (obrigatório)</span>
          </>
        )}
      </label>

      {children}

      {(hint || error || meta) && (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            {hint && !error && (
              <p id={hintId} className="text-xs leading-4 text-[var(--text-muted)]">
                {hint}
              </p>
            )}
            {error && (
              <p
                id={errorId}
                role="alert"
                className="text-xs font-medium leading-4 text-[var(--danger)]"
              >
                {error}
              </p>
            )}
          </div>
          {meta && <div className="shrink-0">{meta}</div>}
        </div>
      )}
    </div>
  )
}

/** Classes base dos controles de formulário (input, textarea, select). */
export function fieldControlClass(invalid: boolean): string {
  return cn(
    'w-full rounded-lg border bg-[var(--surface-card)] px-3 py-2 text-sm text-[var(--text-primary)]',
    'placeholder:text-[var(--text-muted)] transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
    'disabled:cursor-not-allowed disabled:bg-[var(--surface-inset)] disabled:opacity-60',
    invalid
      ? 'border-[var(--danger)]'
      : 'border-[var(--border-strong)] hover:border-[var(--accent)]/60',
  )
}

/** Junta os ids de descrição, ignorando os que não existem. */
export function describedBy(...ids: Array<string | false | undefined>): string | undefined {
  const list = ids.filter((id): id is string => Boolean(id))
  return list.length > 0 ? list.join(' ') : undefined
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: string
  error?: string
  id?: string
  /** Ícone decorativo à esquerda do campo. */
  leadingIcon?: ReactNode
  containerClassName?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    required,
    id,
    leadingIcon,
    containerClassName,
    className,
    'aria-describedby': ariaDescribedBy,
    ...props
  },
  ref,
) {
  const generatedId = useId()
  const controlId = id ?? `input-${generatedId}`
  const hintId = `${controlId}-hint`
  const errorId = `${controlId}-error`

  return (
    <FieldFrame
      controlId={controlId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      hintId={hintId}
      errorId={errorId}
      className={containerClassName}
    >
      <div className="relative">
        {leadingIcon && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-[var(--text-muted)]"
          >
            {leadingIcon}
          </span>
        )}
        <input
          ref={ref}
          id={controlId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(
            hint && !error && hintId,
            error && errorId,
            ariaDescribedBy,
          )}
          className={cn(fieldControlClass(Boolean(error)), 'h-10', leadingIcon && 'pl-9', className)}
          {...props}
        />
      </div>
    </FieldFrame>
  )
})
